import { makeStyles } from "@mui/styles";
import { THEME_COLORS } from "../../context/constants";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    minHeight: "80vh",
    fontFamily: "Lexend, sans-serif",
    padding: "20px",
  },
  // Carousel of past generations
  carouselArea: {
    width: "100%",
    maxWidth: "900px",
    marginBottom: "24px",
  },
  carouselHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  carouselScroll: {
    display: "flex",
    gap: "12px",
    overflowX: "auto" as const,
    paddingBottom: "8px",
    scrollbarWidth: "thin" as const,
    "&::-webkit-scrollbar": {
      height: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#ddd",
      borderRadius: "4px",
    },
  },
  mediaCard: {
    minWidth: "180px",
    maxWidth: "180px",
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: "#f9f9f9",
    border: "1px solid #eee",
    position: "relative" as const,
    flexShrink: 0,
    cursor: "pointer",
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
    "& video": {
      width: "100%",
      height: "120px",
      objectFit: "cover" as const,
      display: "block",
    },
  },
  mediaCardEmpty: {
    minWidth: "180px",
    maxWidth: "180px",
    height: "170px",
    borderRadius: "12px",
    backgroundColor: "#f5f5f5",
    border: "2px dashed #ddd",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  mediaCardInfo: {
    padding: "8px 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  mediaCardPrompt: {
    fontSize: "11px",
    color: "#666",
    fontFamily: "Lexend, sans-serif",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    flex: 1,
    minWidth: 0,
  },
  mediaCardBadge: {
    position: "absolute" as const,
    top: "6px",
    left: "6px",
    backgroundColor: "rgba(0,0,0,0.6)",
    color: "#fff",
    fontSize: "9px",
    fontFamily: "Lexend, sans-serif",
    fontWeight: 500,
    padding: "2px 6px",
    borderRadius: "4px",
    lineHeight: 1.3,
    zIndex: 1,
  },
  mediaCardOverlay: {
    height: "120px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
    gap: "4px",
  },
  // Current generation (above prompt)
  currentGeneration: {
    width: "100%",
    maxWidth: "200px",
    marginBottom: "16px",
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: "#f9f9f9",
    border: "1px solid #eee",
    "& video": {
      width: "100%",
      display: "block",
    },
  },
  currentGenLoading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    gap: "12px",
  },
  // Gallery empty state
  galleryArea: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    width: "100%",
    maxWidth: "900px",
    minHeight: "200px",
    position: "relative" as const,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    opacity: 0.6,
  },
  emptyIcon: {
    width: "80px",
    height: "80px",
    backgroundColor: "#f5f5f5",
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px dashed #ddd",
  },
  modeToggle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "20px",
  },
  promptContainer: {
    width: "100%",
    maxWidth: "700px",
    borderRadius: "16px",
    border: "1px solid #e0e0e0",
    backgroundColor: "#fff",
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  promptInput: {
    width: "100%",
    minHeight: "80px",
    padding: "16px",
    boxSizing: "border-box" as const,
    border: "none",
    outline: "none",
    fontFamily: "Lexend, sans-serif",
    fontSize: "14px",
    resize: "none" as const,
    backgroundColor: "transparent",
    "&::placeholder": {
      color: "#aaa",
    },
  },
  promptToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    borderTop: "1px solid #f0f0f0",
  },
  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  modelChip: {
    backgroundColor: "#f5f5f5",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "4px 12px",
    fontSize: "13px",
    fontWeight: 500,
    fontFamily: "Lexend, sans-serif",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "background-color 0.15s",
    "&:hover": {
      backgroundColor: "#eeeeee",
    },
  },
  // Storyboard shots UI
  shotsContainer: {
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "10px",
    maxHeight: "300px",
    overflowY: "auto" as const,
  },
  shotRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    padding: "10px 12px",
    backgroundColor: "#fafafa",
    borderRadius: "10px",
    border: "1px solid #f0f0f0",
  },
  shotNumber: {
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: "Lexend, sans-serif",
    color: "#888",
    minWidth: "52px",
    paddingTop: "6px",
  },
  shotInput: {
    flex: 1,
    border: "none",
    outline: "none",
    fontFamily: "Lexend, sans-serif",
    fontSize: "13px",
    resize: "none" as const,
    backgroundColor: "transparent",
    minHeight: "36px",
    "&::placeholder": {
      color: "#bbb",
    },
  },
  shotDuration: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    minWidth: "70px",
    paddingTop: "4px",
  },
  shotDurationInput: {
    width: "40px",
    textAlign: "center" as const,
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    padding: "3px 4px",
    fontFamily: "Lexend, sans-serif",
    fontSize: "12px",
    outline: "none",
    backgroundColor: "#fff",
  },
  addShotButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    padding: "8px",
    border: "1px dashed #ddd",
    borderRadius: "8px",
    backgroundColor: "transparent",
    cursor: "pointer",
    fontFamily: "Lexend, sans-serif",
    fontSize: "12px",
    color: "#888",
    transition: "background-color 0.15s",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  charCount: {
    fontSize: "12px",
    color: "#999",
    fontFamily: "Lexend, sans-serif",
  },
  quantityControl: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "2px 4px",
  },
  quantityInput: {
    width: "24px",
    textAlign: "center" as const,
    border: "none",
    outline: "none",
    fontFamily: "Lexend, sans-serif",
    fontSize: "13px",
    backgroundColor: "transparent",
  },
  submitButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: THEME_COLORS.black,
    color: "#fff",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "opacity 0.2s",
    "&:hover": {
      opacity: 0.8,
    },
    "&:disabled": {
      opacity: 0.3,
      cursor: "not-allowed",
    },
  },
  settingsPopover: {
    padding: "16px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
    minWidth: "200px",
  },
  // Modal styles
  modalMediaArea: {
    width: "100%",
    backgroundColor: "#000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
  },
  modalVideo: {
    width: "100%",
    maxHeight: "500px",
    display: "block",
  },
  modalStatusArea: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "48px",
  },
}));

export default useStyles;
